import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router-dom';
import Footer from '../../components/Footer';
import 'wicg-inert';
import ScrollToTop from "../../libs/scrollToTop";
import { useKonami } from 'react-konami-code';
import Nav from '../../components/Nav.js';
import SecondaryNav from '../../components/SecondaryNav';

import ModalOverlay from "../../components/ModalOverlay";

import heroMobileImage from '../../images/articles/PO0131005_WF_BRAND_2HFY24_ISF_DecUpdate17_LP_767x577.jpg';
import heroDesktopImage from '../../images/articles/PO0131005_WF_BRAND_2HFY24_ISF_DecUpdate17_LP_1164x584.jpg';

import { fireTag } from '../../libs/analytics.js';

const NuclearEnergy = ({
    handleEvent,
    closeExitOverlay,
    addExitClickListeners,
    modalOverlay,
}) => {

    const [enableScrollGA, setEnableScrollGA] = useState(false);
    const [shouldBeInert, setShouldBeInert] = useState(false);
    const [gridVisible, setGridVisible] = useState(false);

    const heroOGImage = 'PO0131005_WF_BRAND_2HFY24_ISF_DecUpdate17_LP_ogimage.jpg';

    const toggleInert = () => {
        setShouldBeInert(!shouldBeInert)
    }

    const linkDisclosures = () => {
		const bodyDisclosures = document.querySelectorAll('.body-disclosures');
		const footerDisclosures = document.querySelectorAll('.footer-disclosures');
		bodyDisclosures.forEach(elem => {
			elem.addEventListener('click', (e) => {
				e.preventDefault();
				let disclosureNumber = Number(e.target.innerHTML)
				let targetElement = footerDisclosures[disclosureNumber - 1];
				window.scrollTo({
					left: 0,
					top: targetElement.offsetTop,
					behavior: 'smooth'
				});
				window.setTimeout(() => {
					targetElement.focus();
				}, 1000);
			});
		});

		footerDisclosures.forEach(elem => {
			elem.addEventListener('click', (e) => {
				e.preventDefault();
				let disclosureNumber = Number(e.target.innerHTML)
				let targetElement = bodyDisclosures[disclosureNumber - 1];
				fireTag('A2',disclosureNumber);
				window.scrollTo({
					left: 0,
					top: targetElement.offsetTop,
					behavior: 'smooth'
				});
				window.setTimeout(() => {
					targetElement.focus();
				}, 1000);
			});
		});
	}

	useEffect(() => {
		linkDisclosures();
		addExitClickListeners();

		document.addEventListener('DOMContentLoaded', function() {
			// Your code to run when the DOM is fully loaded
			let script = document.createElement('script');
			script.src = "https://public.flourish.studio/resources/embed.js";
	
			document.querySelector('.flourish-embed').appendChild(script);
		  });

	}, []); // empty array says only use this effect once

    let mastheadImage = window.innerWidth < 768 ? heroMobileImage : heroDesktopImage;

    return (
		<>
			<Helmet>
				<title>Nuclear energy is back in the spotlight</title>
				<meta
					name="description"
					content="The financial sector can support big tech companies pursuing a nuclear-powered future."
				/>
				<meta property="og:title" content="Nuclear energy is back in the spotlight"/>
				<meta
					property="og:description"
					content="The financial sector can support big tech companies pursuing a nuclear-powered future."
				/>
				<meta property="og:image" content={`https://welcome.wf.com/impact/images/articles/${heroOGImage}`}/>

				<meta property="twitter:title" content="Nuclear energy is back in the spotlight"/>
				<meta
					property="twitter:description"
					content="The financial sector can support big tech companies pursuing a nuclear-powered future."
				/>
				i
			</Helmet>
			{/*TODO: isMobile should be passed in from route, not calc'd here */}

			<Nav handleEvent={handleEvent} isNotHomePage={false} activePage="articles" isMobile={window.innerWidth < 768} showExternalNav toggleInert={toggleInert} shouldBeInert={shouldBeInert}/>

			{!window.innerWidth < 768 && (
				<SecondaryNav page="Articles" shouldBeInert={shouldBeInert} activePage="articles"/>
			)}


			<main className="flex-container">

				<div className="flex-row">
					<div className="flex-col-sm-12 flex-col-md-8 text-center headline">
						<h1 id="articles-main-h1" style={{marginBottom: 10}}>
                        Nuclear energy is back in the spotlight                         
                        </h1>
						<h2 className="summary">
                        The financial sector can support big tech companies pursuing a nuclear-powered future.
                        </h2>
					</div>

				</div>

				<div className="flex-row full-width-md">
					<div className="flex-col-lg-10 flex-col-md-10 text-center hero-container">
						<img src={mastheadImage} alt="Water vapor is released from two nuclear power plant cooling towers beside a river at sunset."/>
					</div>
				</div>
                
				<div className="flex-row">
					<div className="flex-col-md-8 flex-col-sm-12 text-center article-intro info-graphic">
                    <div className="by-line">
							<p>By Jeffrey Schub, <span>sustainable finance integration leader at Wells Fargo</span></p>
						</div>
						<div className="article-info">
							<div>
								<p>December 20, 2024</p>
							</div>
							<div>
								<p>5-minute read</p>
							</div>
						</div>
						<p>Nuclear energy — the <a onClick={()=> fireTag("A1","largest source of low-emission power")} target="_blank" href="https://www.energy.gov/ne/articles/advantages-and-challenges-nuclear-energy" rel="noreferrer">largest source of low-emission power</a> in the U.S. — is making a comeback. For decades, significant barriers like safety concerns, high costs, and regulatory red tape have stymied the industry’s growth. But today, the finance sector is emerging as a key player to support corporations that seek to use nuclear power to meet the energy demands of artificial intelligence, while minimizing their carbon footprints.</p>
                        <p>
                        The <a onClick={()=> fireTag("A1","nuclear fission")} target="_blank" href="https://climate.mit.edu/explainers/nuclear-energy" rel="noreferrer">nuclear fission</a> process is among the most efficient ways to generate large-scale power without a carbon output. At its core, <a onClick={()=> fireTag("A1","nuclear energy")} target="_blank" href="https://www.iaea.org/newscenter/news/what-is-nuclear-energy-the-science-of-nuclear-power" rel="noreferrer">nuclear energy</a> harnesses the energy released when atoms, such as uranium, are split inside a reactor. This process generates an immense amount of heat that produces steam and powers turbines to produce electricity — all without the carbon emissions from coal or gas.
                        </p>
                    <h2 className="subhead" style={{marginTop: '1rem'}}>
                        Innovative technologies deliver a new age of nuclear reactors                       
                    </h2>
                    <p>
                    Historically, nuclear power plants were expensive and time-consuming to build, but new reactor designs have the potential to transform the industry. Plant Vogtle in Georgia finished construction of the first new U.S. reactors in over 30 years. The project secured up to $12 billion in loan guarantees from the <a onClick={()=> fireTag("A1","Department of Energy")} target="_blank" href="https://www.energy.gov/ne/articles/5-things-you-should-know-about-plant-vogtle" rel="noreferrer">Department of Energy</a> (DOE) and is <a onClick={()=> fireTag("A1","the first U.S. deployment")} target="_blank" href="https://www.energy.gov/lpo/articles/plant-vogtle-unit-3-enters-commercial-operations-bringing-carbon-free-nuclear-energy" rel="noreferrer">the first U.S. deployment</a> of a new generation of reactor technology featuring advanced safety systems. Vogtle is expected to generate more than 17 million megawatt-hours of electricity.  					</p>
                    <p>
                    Meanwhile, <a onClick={()=> fireTag("A1","small modular reactors")} target="_blank" href="https://www.weforum.org/stories/2024/11/accelerating-new-nuclear-and-small-modular-reactor-deployment/" rel="noreferrer">small modular reactors</a>, or SMRs, may offer a new, simpler pathway for nuclear development. These compact reactors can be built in factories, assembled on-site, and scaled to meet specific energy needs. SMRs occupy a much smaller physical footprint than a traditional nuclear power plant, making them ideal for space-constrained communities or industries. TerraPower, an energy company backed by Microsoft co-founder Bill Gates, <a onClick={()=> fireTag("A1","broke ground")} target="_blank" href="https://www.terrapower.com/terrapower-begins-construction-in-wyoming" rel="noreferrer">broke ground</a> on an SMR in Wyoming last June. Additionally, the DOE <a onClick={()=> fireTag("A1","opened applications")} target="_blank" href="https://www.energy.gov/oced/generation-iii-small-modular-reactor-program" rel="noreferrer">opened applications</a> in October for $900 million in funding to support the deployment of SMRs. 
                    </p>
                    <p>
                    These advances in nuclear energy come as energy-hungry data centers bring data-driven technologies and artificial intelligence online. The <a onClick={()=> fireTag("A1","DOE estimates")} target="_blank" href="https://www.energy.gov/policy/articles/clean-energy-resources-meet-data-center-electricity-demand#op1" rel="noreferrer">DOE estimates</a> that — after remaining flat since the early 2000s — U.S. total energy demand could grow 15-20% in the next decade. This has led to a flurry of corporate activity as companies search for ways to power their data centers while also meeting their decarbonization goals. 
                    </p>
                    <div className="flourish-embed flourish-chart" data-src="visualisation/12775625"></div>  
                    <p>According to the <a onClick={()=> fireTag("A1","National Renewable Energy Laboratory")} target="_blank" href="https://www.nrel.gov/analysis/re-futures.html#:~:text=Renewable%20electricity%20generation%20from%20technologies,every%20region%20of%20the%20country." rel="noreferrer">National Renewable Energy Laboratory</a>, renewable electricity generation from existing technologies could supply 80% of total U.S. electricity generation in 2050. However, wind and solar face limitations for consistent energy production due to dependance on weather conditions and large land requirements.</p>
                    <p>As a result, tech companies are turning to nuclear energy. Google has <a onClick={()=> fireTag("A1","announced plans")} target="_blank" href="https://www.reuters.com/technology/artificial-intelligence/google-buy-power-small-modular-nuclear-reactor-company-kairos-ai-needs-2024-10-14/" rel="noreferrer">announced plans</a> to buy power from small reactors in the U.S., and Amazon <a onClick={()=> fireTag("A1","reached an agreement")} target="_blank" href="https://www.aboutamazon.com/news/sustainability/amazon-nuclear-small-modular-reactor-net-carbon-zero" rel="noreferrer">reached an agreement</a> with Energy Northwest to develop SMRs. Microsoft is also exploring ways to <a onClick={()=> fireTag("A1","integrate SMRs and microreactors")} target="_blank" href="https://www.neimagazine.com/news/microsoft-seeks-manager-to-develop-smrs-to-power-its-data-centres-11189098/" rel="noreferrer">integrate SMRs and microreactors</a> to power its data centers, while also looking to <a onClick={()=> fireTag("A1","reopen a reactor")} target="_blank" href="https://www.cnn.com/2024/09/20/energy/three-mile-island-microsoft-ai/index.html" rel="noreferrer">reopen a reactor</a> at Three Mile Island in Pennsylvania.</p>
                    <h2 className="subhead" style={{marginTop: '1rem'}}>
                    Federal and private financial support fuels growth in nuclear energy         
                    </h2>
                    <p>
                    These technological advancements are spurred by federal support and nuclear energy investments in the private sector. Nuclear power plants in the U.S. contribute <a onClick={()=> fireTag("A1","billions of dollars")} target="_blank" href="https://www.energy.gov/ne/articles/advantages-and-challenges-nuclear-energy" rel="noreferrer">billions of dollars</a> a year to local economies through federal and state tax revenues.
                    </p>
                    <p>
                    The federal government has stepped up its support for nuclear energy. DOE provides funding opportunities and loan guarantees, while the <a onClick={()=> fireTag("A1","Inflation Reduction Act")} target="_blank" href="https://home.treasury.gov/news/press-releases/jy1830#:~:text=The%20Inflation%20Reduction%20Act%20modifies%20and%20extends%20the%20Renewable%20Energy,energy%20production%20in%20energy%20communities." rel="noreferrer">Inflation Reduction Act</a> offers the same production and investment tax credits that fueled a boom in wind and solar energy. For U.S. companies investing in clean energy, banks can help structure solutions to reduce tax liability for clean energy projects.                    </p>
                    <p>
                    The financial sector also plays a critical role in supporting customers and clients who seek access to capital to adopt nuclear technology. Banks can facilitate private placements, equity investments, and direct lending. In March 2024, Constellation, the nation’s largest producer of carbon-free energy, issued a <a onClick={()=> fireTag("A1","$900 million green bond")} target="_blank" href="https://www.constellationenergy.com/newsroom/2024/Constellation-Offers-Nations-First-Corporate-Green-Bond-for-Nuclear-Energy.html" rel="noreferrer">$900 million green bond</a> for nuclear energy — a first of its kind in the U.S. — signaling investor support of nuclear as a form of sustainable investing.                    </p>
                    <p>
                    Nuclear energy is helping to revolutionize the energy landscape. With new reactor technologies, government incentives, and comprehensive financial support, a solution from the past can help big tech companies meet modern-day, energy-intensive challenges.
                    </p>
					</div>
				</div>
			</main>


			<Footer id="footer" footerStyles="global" shouldBeInert={shouldBeInert} activePage="sustainability" enableScrollGA={enableScrollGA}/>

			{modalOverlay && modalOverlay.open && (
				<ModalOverlay closeExitOverlay={closeExitOverlay} modalOverlay={modalOverlay}/>
			)}
		</>
    );

};

export default withRouter(NuclearEnergy);



